import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { Skeleton } from "@/components/Skeleton";
import { FeedRow } from "@/components/audit/FeedRow";
import { RawRequestLog } from "@/components/audit/RawRequestLog";
import type { FeedItem } from "@/lib/auditKinds";

interface Anomaly {
  kind: string;
  user_id: string | null;
  user_name: string | null;
  label: string;
  detail: string;
  tone: "rose" | "amber";
}

interface SecurityResponse {
  anomalies: Anomaly[];
  events: FeedItem[];
}

export function SecurityTab({ range }: { range: string }) {
  const [data, setData] = useState<SecurityResponse | null>(null);
  const [rawOpen, setRawOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    setData(null);
    api<SecurityResponse>(`/api/audit/security?range=${range}`)
      .then((r) => alive && setData(r))
      .catch(() => alive && setData({ anomalies: [], events: [] }));
    return () => {
      alive = false;
    };
  }, [range]);

  return (
    <div className="space-y-5">
      {/* flags */}
      {data === null ? (
        <Skeleton className="h-20 w-full rounded-xl" />
      ) : data.anomalies.length === 0 ? (
        <div className="card px-5 py-4 text-sm text-slate-500">
          Nothing unusual in this window.
        </div>
      ) : (
        <div className="space-y-2">
          {data.anomalies.map((a, i) => (
            <div
              key={`${a.kind}-${a.user_id}-${i}`}
              className={`card px-4 py-3 border-l-2 ${
                a.tone === "rose" ? "border-l-rose-500/60" : "border-l-amber-500/60"
              }`}
            >
              <div className="flex items-start gap-3">
                <span
                  className={`text-[13px] leading-5 ${
                    a.tone === "rose" ? "text-rose-300" : "text-amber-300"
                  }`}
                >
                  ⚠
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] text-slate-200">
                    {a.user_id ? (
                      <Link
                        to={`/audit/users/${a.user_id}`}
                        className="font-medium hover:underline decoration-white/20"
                      >
                        {a.user_name}
                      </Link>
                    ) : (
                      <span className="font-medium">{a.user_name ?? "—"}</span>
                    )}
                    <span className="text-slate-400"> — {a.label}</span>
                  </div>
                  <div className="text-[11.5px] text-slate-500 mt-0.5">{a.detail}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* sensitive events */}
      <section className="card overflow-hidden">
        <div className="px-5 py-3 border-b border-white/5">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            Destructive &amp; outward-facing
          </div>
          <div className="text-[11.5px] text-slate-500 mt-1">
            Deletions, teardowns, and links that expose listings outside the team.
          </div>
        </div>
        <div className="px-5">
          {data === null ? (
            <div className="py-4 space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-7 w-full" />
              ))}
            </div>
          ) : data.events.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500">
              Nothing destructive in this window.
            </div>
          ) : (
            data.events.map((e) => <FeedRow key={e.id} item={e} />)
          )}
        </div>
      </section>

      {/* raw log — collapsed, and not fetched until opened */}
      <section className="card overflow-hidden">
        <button
          type="button"
          onClick={() => setRawOpen((v) => !v)}
          className="w-full px-5 py-3 flex items-center justify-between text-left hover:bg-white/[0.02] transition"
        >
          <span>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              Raw request log
            </span>
            <span className="block text-[11.5px] text-slate-500 mt-1">
              Every authenticated API call, reads included — mostly page polling.
              For investigations, not for reading.
            </span>
          </span>
          <span className="text-slate-500 text-sm flex-shrink-0 ml-3">
            {rawOpen ? "Hide" : "Show"}
          </span>
        </button>
        {rawOpen && (
          <div className="px-5 pb-5 pt-1 border-t border-white/5">
            <RawRequestLog open={rawOpen} />
          </div>
        )}
      </section>
    </div>
  );
}
